import {
  limitNumberInputDigits,
  SAFE_NUMBER_INPUT_PLACEHOLDER,
  safeNumberInputProps,
} from '@/constants/numberInput';
import {
  DateRangePicker,
  MUISelect,
  MUITextField,
} from '@abb/arcadia-ui-react';
import {
  ProColumns,
  ProRenderFieldPropsType,
} from '@ant-design/pro-components';
import { InputNumber } from 'antd';
import type { ReactNode } from 'react';

type CValueType = 'abbText' | 'abbSelect' | 'abbDatePicker' | 'digit';

function formatDigitText(text: unknown, precision?: number) {
  if (text === null || text === undefined || text === '') return '-';
  const num = Number(text);
  if (!Number.isFinite(num)) return text as ReactNode;
  if (typeof precision === 'number') {
    return num.toLocaleString(undefined, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    });
  }
  return text as ReactNode;
}

export const valueTypeMap: Partial<
  Record<CValueType, ProRenderFieldPropsType>
> = {
  /**
   * 覆盖内置 digit：按位数截断输入。
   * 使用 antd InputNumber，避免依赖 @ant-design/pro-field 内部路径（流水线无法 resolve）。
   */
  digit: {
    render: (text, props) => (
      <>{formatDigitText(text, props.fieldProps?.precision)}</>
    ),
    renderFormItem: (text, props) => {
      const columnParser = props.fieldProps?.parser;
      const columnRenderFormItem = (
        props as {
          renderFormItem?: (
            text: any,
            props: any,
            dom: ReactNode,
          ) => ReactNode;
        }
      ).renderFormItem;

      const fieldProps = {
        min: 0,
        style: { width: '100%' as const },
        ...safeNumberInputProps,
        ...props.fieldProps,
        parser: (displayValue: string | undefined) => {
          if (columnParser) return columnParser(displayValue);
          return limitNumberInputDigits(displayValue);
        },
        placeholder:
          props.fieldProps?.placeholder || SAFE_NUMBER_INPUT_PLACEHOLDER,
      };

      const input = <InputNumber {...fieldProps} />;

      if (columnRenderFormItem) {
        return <>{columnRenderFormItem(text, props, input)}</>;
      }
      return input;
    },
  },
  abbText: {
    render: (text) => text || '-',
    renderFormItem: (_, { value, fieldProps }) => (
      <MUITextField
        orientation="column"
        placeholder="请输入"
        size={32}
        value={value}
        {...fieldProps}
      />
    ),
  },
  abbSelect: {
    render: (text) => text || '-',
    renderFormItem: (_, { value, fieldProps, onChange }) => {
      const {
        dropdownLOV = [],
        placeholder = '请选择',
        onChange: fieldChange,
        ...rest
      } = fieldProps;

      return (
        <MUISelect
          dropdownLOV={dropdownLOV}
          placeholder={placeholder}
          size={32}
          name="dropdown"
          selectedItem={[]}
          {...fieldProps}
        />
      );
    },
  },
  abbDatePicker: {
    render: (text) => text || '-',
    renderFormItem: (_, { value, fieldProps }) => (
      <DateRangePicker
        initialDateRange={{}}
        placeholder="Start Date - End Date"
        variant="date-range-picker"
        isSingleCalender
        {...fieldProps}
      />
    ),
  },
};

export type CProColumns<T = any, U = any> =
  | ProColumns<T, U>
  | {
      valueType: ProColumns['valueType'] | CValueType;
    };
